// src/pages/Paketler.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, TextField, FormControl, ListItemText, Avatar,
  Select, MenuItem, Dialog, Checkbox, Snackbar, Switch, FormControlLabel,
  InputLabel, DialogActions, DialogContent, DialogTitle, Typography, FormGroup
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import MuiAlert from "@mui/material/Alert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";

const PackagesPage = () => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [packagesList, setPackagesList] = useState([]);
  const [counts, setCounts] = useState({ active: 0, passive: 0, total: 0 });
  const [botList, setBotList] = useState([]);
  const [viewMode, setViewMode] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const getQuotaValue = (quotas = [], type) => {
    const q = (quotas || []).find((x) => x && x.type === type);
    return q?.limitValue ?? "";
  };

  const filteredRows = packagesList.filter((row) => {
    const matchesSearch = (row.name || "").toLowerCase().includes((searchText || "").toLowerCase());
    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "active" && row.isActive) ||
      (statusFilter === "passive" && !row.isActive);
    return matchesSearch && matchesStatus;
  });

  const emptyForm = {
    name: "",
    description: "",
    price: "",
    messageLimit: "",
    webSearchLimit: "",
    voiceMinutes: "",
    duration: "",
    bots: [],
    image: null,
    isActive: true,
    requiresCardOnTrial: false,
  };

  const [formData, setFormData] = useState(emptyForm);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get("/api/packages", {
        headers: getAuthHeaders()
      });
      const allPackages = res.data.data;

      setPackagesList(allPackages);

      const active = allPackages.filter((p) => p.isActive).length;
      const passive = allPackages.filter((p) => !p.isActive).length;
      const total = allPackages.length;

      setCounts({ active, passive, total });
    } catch (err) {
      console.error("Paketleri alırken hata:", err);
    }
  }, [getAuthHeaders]);

  const fetchBots = useCallback(async () => {
    try {
      const res = await axios.get("/api/bots", {
        headers: getAuthHeaders()
      });
      setBotList(res.data);
    } catch (err) {
      console.error("Botları alırken hata:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchBots();
    fetchPackages();
  }, [fetchBots, fetchPackages]);

  const cards = [
    {
      label: "Aktif Paketler",
      value: counts.active,
      icon: <CheckCircleIcon color="success" fontSize="large" />
    },
    {
      label: "Pasif Paketler",
      value: counts.passive,
      icon: <CancelIcon color="error" fontSize="large" />
    },
    {
      label: "Toplam Paket",
      value: counts.total,
      icon: <Inventory2Icon color="primary" fontSize="large" />
    },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleView = (row) => {
    setFormData({
      name: row.name,
      description: row.description,
      price: row.price,
      messageLimit: getQuotaValue(row.quotas || [], "MESSAGE"),
      webSearchLimit: getQuotaValue(row.quotas || [], "WEB_SEARCH"),
      voiceMinutes: getQuotaValue(row.quotas || [], "VOICE"),
      duration: row.duration,
      bots: (row.bots || []).map((b) => (typeof b === "object" ? b._id : b)),
      image: null,
      isActive: row.isActive,
      requiresCardOnTrial: !!row.requiresCardOnTrial,
      _id: row._id,
    });
    setPreview(row.image || null);
    setViewMode(true);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setFormData({
      name: row.name,
      description: row.description,
      price: row.price,
      messageLimit: getQuotaValue(row.quotas || [], "MESSAGE"),
      webSearchLimit: getQuotaValue(row.quotas || [], "WEB_SEARCH"),
      voiceMinutes: getQuotaValue(row.quotas || [], "VOICE"),
      duration: row.duration,
      bots: (row.bots || []).map((b) => (typeof b === "object" ? b._id : b)),
      image: null,
      isActive: row.isActive,
      requiresCardOnTrial: !!row.requiresCardOnTrial,
      _id: row._id,
    });
    setPreview(row.image || null);
    setViewMode(false);
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Number(formData.duration) < 1) {
      alert("Paket süresi 1 günden az olamaz.");
      return;
    }

    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("description", formData.description);
      form.append("price", String(formData.price));
      form.append("duration", String(formData.duration));
      form.append("bots", JSON.stringify(formData.bots || []));

      const quotas = [
        { type: "MESSAGE", limitType: "COUNT", limitValue: Number(formData.messageLimit) || 0 },
        { type: "WEB_SEARCH", limitType: "COUNT", limitValue: Number(formData.webSearchLimit) || 0 },
        { type: "VOICE", limitType: "MINUTES", limitValue: Number(formData.voiceMinutes) || 0 },
      ];
      form.append("quotas", JSON.stringify(quotas));

      if (formData.image) form.append("image", formData.image);

      form.append("isActive", String(!!formData.isActive));
      form.append("requiresCardOnTrial", String(!!formData.requiresCardOnTrial));

      if (formData._id) {
        await axios.put(`/api/packages/${formData._id}`, form, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data"
          },
        });
        setSnackbar({ open: true, message: "Paket güncellendi!", severity: "success" });
      } else {
        await axios.post("/api/packages", form, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data"
          },
        });
        setSnackbar({ open: true, message: "Paket başarıyla eklendi!", severity: "success" });
      }

      await fetchPackages();
      setOpen(false);
      setFormData(emptyForm);
      setPreview(null);
    } catch (error) {
      setSnackbar({ open: true, message: "Paket eklenemedi!", severity: "error" });
      console.error("Paket ekleme hatası:", error);
      alert("Bir hata oluştu");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await axios.post(
        `/api/packages/${id}/duplicate`,
        {},
        {
          withCredentials: true,
          headers: getAuthHeaders()
        }
      );
      setSnackbar({
        open: true,
        message: `Paket kopyalandı: ${res.data.data.name}`,
        severity: "success",
      });
      fetchPackages();
    } catch (error) {
      console.error("Kopyalama hatası:", error);
      setSnackbar({ open: true, message: "Paket kopyalanamadı!", severity: "error" });
    }
  };

  const handleClose = () => setOpen(false);

  return (
    <Box p={3}>
      {/* Kartlar */}
      <Box display="flex" justifyContent="center" gap={3} flexWrap="wrap" mb={4}>
        {cards.map((card, index) => (
          <Box
            key={index}
            width={250}
            p={2}
            boxShadow={3}
            borderRadius={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            bgcolor="white"
          >
            {card.icon}
            <Typography variant="h6" mt={1}>{card.label}</Typography>
            <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
          </Box>
        ))}
      </Box>

      <Button
        variant="contained"
        onClick={() => {
          setPreview(null);
          setViewMode(false);
          setFormData(emptyForm);
          setOpen(true);
        }}
        sx={{ mb: 3 }}
      >
        +Yeni Paket Ekle
      </Button>

      <Box display="flex" justifyContent="left" mb={2} flexWrap="wrap" gap={2} width={"80%"}>
        <TextField
          label="Paket Ara"
          variant="outlined"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ width: 240 }}
        />

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Durum</InputLabel>
          <Select value={statusFilter} label="Durum" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="active">Aktif</MenuItem>
            <MenuItem value="passive">Pasif</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {viewMode ? "Paket Detayı" : formData._id ? "Paket Düzenle" : "Yeni Paket Ekle"}

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box mt={2}>
            <Typography fontWeight="bold" mb={1}>Paket Görseli</Typography>

            <Button variant="outlined" component="label" disabled={viewMode}>
              Görsel Yükle
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) {
                    setFormData((prev) => ({ ...prev, image: file }));
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
            </Button>

            {preview && (
              <Box mt={2} position="relative">
                <img
                  src={preview}
                  alt="Önizleme"
                  style={{
                    width: "100%",
                    maxHeight: "200px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  disabled={viewMode}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, image: null }));
                    setPreview(null);
                  }}
                  sx={{ mt: 1 }}
                >
                  Resmi Kaldır
                </Button>
              </Box>
            )}
          </Box>

          <TextField
            margin="dense"
            label="Paket Adı"
            name="name"
            sx={{ width: "50%" }}
            value={formData.name}
            onChange={handleChange}
            disabled={viewMode}
          />
          <TextField
            sx={{ width: "80%" }}
            margin="dense"
            label="Paket Açıklaması"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={viewMode}
          />
          <TextField
            sx={{ width: "60%" }}
            margin="dense"
            label="Paket Ücreti (₺)"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            disabled={viewMode}
          />

          {/* Kotalar */}
          <Typography fontWeight="bold" mt={2} mb={1}>Kotalar</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              sx={{ width: "30%" }}
              margin="dense"
              label="Mesaj (adet)"
              name="messageLimit"
              type="number"
              value={formData.messageLimit}
              onChange={handleChange}
              disabled={viewMode}
            />
            <TextField
              sx={{ width: "30%" }}
              margin="dense"
              label="Web Search (adet)"
              name="webSearchLimit"
              type="number"
              value={formData.webSearchLimit}
              onChange={handleChange}
              disabled={viewMode}
            />
            <TextField
              sx={{ width: "30%" }}
              margin="dense"
              label="Voice (dakika)"
              name="voiceMinutes"
              type="number"
              value={formData.voiceMinutes}
              onChange={handleChange}
              disabled={viewMode}
            />
          </Box>

          <TextField
            sx={{ width: "60%" }}
            margin="dense"
            label="Paket Süresi (gün)"
            name="duration"
            type="number"
            value={formData.duration}
            onChange={handleChange}
            disabled={viewMode}
          />

          {viewMode ? (
            <TextField
              label="Kullanılacak Botlar"
              value={(formData.bots || [])
                .map((id) => {
                  const found = botList.find((bot) => bot._id === id);
                  return found?.name || id;
                })
                .join(", ")}
              fullWidth
              margin="dense"
              InputProps={{ readOnly: true }}
              disabled
            />
          ) : (
            <FormControl fullWidth margin="dense">
              <InputLabel>Kullanılacak Botlar</InputLabel>
              <Select
                multiple
                name="bots"
                value={formData.bots}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    bots: e.target.value,
                  }))
                }
                renderValue={(selected) =>
                  (selected || [])
                    .map((id) => {
                      const found = botList.find((bot) => bot._id === id);
                      return found?.name || id;
                    })
                    .join(", ")
                }
              >
                {botList.map((bot) => (
                  <MenuItem key={bot._id} value={bot._id}>
                    <Checkbox checked={(formData.bots || []).includes(bot._id)} />
                    <ListItemText primary={bot.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormGroup row sx={{ mt: 2, columnGap: 3, alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.isActive}
                  disabled={viewMode}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                  color="primary"
                />
              }
              label={formData.isActive ? "Aktif" : "Pasif"}
              sx={{ mt: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.requiresCardOnTrial}
                  disabled={viewMode}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, requiresCardOnTrial: e.target.checked }))
                  }
                  color="primary"
                />
              }
              label="Deneme için kart zorunlu"
              sx={{ mt: 2 }}
            />
          </FormGroup>
        </DialogContent>

        {!viewMode && (
          <DialogActions>
            <Button onClick={handleClose}>İptal</Button>
            <Button variant="contained" onClick={handleSubmit}>Kaydet</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* DataGrid */}
      <Box sx={{ width: "97%", overflowX: "auto", mb: 2, rowHeight: 50 }}>
        <DataGrid
          rows={filteredRows}
          rowHeight={64}
          getRowId={(row) => row._id}
          columns={[
            {
              field: "image",
              headerName: "Görsel",
              flex: 0.2,
              renderCell: (params) => (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                  <Avatar alt={params.row.name} src={params.row.image} />
                </Box>
              )
            },
            { field: "name", headerName: "Paket Adı", flex: 1 },
            { field: "description", headerName: "Açıklama", flex: 1.4 },
            { field: "price", headerName: "Ücret", flex: 0.8 },
            {
              field: "quotaInfo",
              headerName: "Kotalar",
              flex: 1.2,
              renderCell: (params) => {
                const quotas = params.row.quotas || [];
                const msg = quotas.find((q) => q.type === "MESSAGE")?.limitValue ?? "-";
                const ws = quotas.find((q) => q.type === "WEB_SEARCH")?.limitValue ?? "-";
                const vc = quotas.find((q) => q.type === "VOICE")?.limitValue ?? "-";
                return `Msg: ${msg} • Web: ${ws} • Voice: ${vc}dk`;
              }
            },
            {
              field: "isActive",
              headerName: "Durum",
              flex: 0.8,
              renderCell: (params) => (
                <span style={{ color: params.value ? "green" : "gray", fontWeight: 500 }}>
                  {params.value ? "Aktif" : "Pasif"}
                </span>
              )
            },
            {
              field: "actions",
              headerName: "İşlemler",
              flex: 1,
              renderCell: (params) => (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center" }} gap={1}>
                  <Tooltip title="Detay">
                    <IconButton size="small" onClick={() => handleView(params.row)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Düzenle">
                    <IconButton size="small" onClick={() => handleEdit(params.row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kopyala">
                    <IconButton size="small" color="primary" onClick={() => handleDuplicate(params.row._id)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedIdToDelete(params.row._id);
                        setConfirmDeleteOpen(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            }
          ]}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Paket Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu paketi silmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Vazgeç</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              try {
                await axios.delete(`/api/packages/${selectedIdToDelete}`, {
                  headers: getAuthHeaders()
                });
                setSnackbar({ open: true, message: "Paket silindi", severity: "success" });
                fetchPackages();
              } catch (error) {
                console.error("Silme hatası:", error);
                setSnackbar({ open: true, message: "Silme sırasında hata oluştu", severity: "error" });
              }
              setConfirmDeleteOpen(false);
              setSelectedIdToDelete(null);
            }}
          >
            Evet, Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackagesPage;